import React, { useEffect, useRef, useState } from 'react';

/**
 * The two steps after a reset code has been emailed: prove you read it, then choose a password.
 *
 * <p>Built on the site's own .overlay and .modal-box classes rather than inline styles. Two
 * reasons: it then looks like the rest of the site without restating the palette, and index.css
 * carries a rule that forces `max-width: none !important` onto anything with an inline
 * max-width — a card styled inline stretches to the full width of the window.
 */

const heading = {
  fontFamily: "'Cinzel', serif", fontSize: 18, color: '#C9A558',
  letterSpacing: '0.1em', margin: 0,
};

const blurb = {
  fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#BA9D7C',
  lineHeight: 1.65, margin: '10px 0 22px',
};

const label = {
  display: 'block', fontFamily: "'Montserrat', sans-serif", fontSize: 10.5,
  letterSpacing: '0.16em', textTransform: 'uppercase', color: '#9E7D42', marginBottom: 9,
};

const field = {
  width: '100%', padding: '13px 14px', borderRadius: 8,
  border: '1px solid rgba(201,165,88,0.25)', background: 'rgba(0,0,0,0.25)',
  fontFamily: "'Montserrat', sans-serif", fontSize: 15, color: '#EDD9BC', outline: 'none',
};

const primary = {
  width: '100%', padding: '14px 18px', marginTop: 22, borderRadius: 8, border: 'none',
  background: 'linear-gradient(135deg, #C9A558 0%, #B98F3E 55%, #8B6914 100%)',
  color: '#1a1206', fontFamily: "'Cinzel', serif", fontSize: 13,
  letterSpacing: '0.16em', textTransform: 'uppercase', cursor: 'pointer', fontWeight: 700,
};

const quiet = {
  display: 'block', width: '100%', marginTop: 14, background: 'none', border: 'none',
  color: '#BA9D7C', fontFamily: "'Montserrat', sans-serif", fontSize: 12.5,
  letterSpacing: '0.06em', cursor: 'pointer', textAlign: 'center',
};

const problem = {
  background: 'rgba(150,50,40,0.18)', border: '1px solid rgba(200,90,80,0.4)', color: '#F0B9B2',
  borderRadius: 8, padding: '10px 12px', marginBottom: 18,
  fontFamily: "'Montserrat', sans-serif", fontSize: 12.5, lineHeight: 1.5,
};

function Shell({ title, description, children, onClose }) {
  // Escape closes, and so does the backdrop. A reset dialog whose only exit is success traps
  // anyone who mistyped their address a screen ago.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="modal-box"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <h2 style={heading}>{title}</h2>
        <p style={blurb}>{description}</p>
        {children}
      </div>
    </div>
  );
}

/** Step one: the six digits from the email. */
export function ResetCodeModal({ email, onVerified, onResend, onClose }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const input = useRef(null);

  useEffect(() => { input.current?.focus(); }, []);

  const ready = code.length === 6 && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await onVerified(code.trim());
    setLoading(false);
    if (!result.success) setError(result.error || 'That code is not right.');
  };

  return (
    <Shell
      title="Enter your code"
      description={`We sent a six-digit code to ${email}. It expires in 15 minutes.`}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        {error && <div style={problem}>{error}</div>}
        <label style={label} htmlFor="reset-code">Six-digit code</label>
        <input
          ref={input}
          id="reset-code"
          // A numeric keypad on phones, and iOS and Android can offer the code straight from
          // the email rather than making anyone memorise it.
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          style={{ ...field, letterSpacing: '0.5em', fontSize: 24, textAlign: 'center', fontFamily: "'Cinzel', serif" }}
          required
        />
        <button type="submit" style={{ ...primary, opacity: ready ? 1 : 0.45, cursor: ready ? 'pointer' : 'default' }}
                disabled={!ready}>
          {loading ? 'Checking…' : 'Continue'}
        </button>
        <button
          type="button"
          style={quiet}
          onClick={async () => { await onResend(); setResent(true); setError(''); setCode(''); }}
        >
          {resent ? 'A new code is on its way' : 'Send me another code'}
        </button>
        <button type="button" style={quiet} onClick={onClose}>Cancel</button>
      </form>
    </Shell>
  );
}

/** Step two: the new password, reachable only once the code has been accepted. */
export function NewPasswordModal({ email, onSubmit, onClose }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const input = useRef(null);

  useEffect(() => { input.current?.focus(); }, []);

  const mismatch = confirm.length > 0 && password !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Use at least 8 characters.'); return; }
    if (password !== confirm) { setError('Both entries need to match.'); return; }
    setLoading(true);
    const result = await onSubmit(password);
    setLoading(false);
    if (!result.success) setError(result.error || 'Could not set the new password.');
  };

  return (
    <Shell
      title="Choose a new password"
      description={`Setting a new password for ${email}. Use it the next time you sign in.`}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        {error && <div style={problem}>{error}</div>}
        <div style={{ marginBottom: 18 }}>
          <label style={label} htmlFor="new-password">New password</label>
          <input
            ref={input} id="new-password" type="password" autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            style={field} placeholder="At least 8 characters" required
          />
        </div>
        <div>
          <label style={label} htmlFor="confirm-password">Confirm password</label>
          <input
            id="confirm-password" type="password" autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            style={{ ...field, borderColor: mismatch ? 'rgba(200,90,80,0.55)' : field.border }}
            placeholder="Type it again" required
          />
        </div>
        <button type="submit" style={{ ...primary, opacity: loading ? 0.45 : 1 }} disabled={loading}>
          {loading ? 'Saving…' : 'Set new password'}
        </button>
        <button type="button" style={quiet} onClick={onClose}>Cancel</button>
      </form>
    </Shell>
  );
}
